import {
    BotMessageObject,
    AgentMessageObject,
    ResponseObject,
    HistoryMessage,
    ComponentInterface,
    Chat
} from "../types/chat";
import {waitForSocketConnection, trySendMessage} from "./utils/socketUtils";
import UserData from "./user/UserData";
import AbstractChatConnector from "./chat/connector/abstractChatConnector";
import {ChatHistoryHandler} from "./chat/chatHistoryHandler";
import {isBotMessageObject} from "./utils/typeUtils";
import {MessageFactory} from "./factory/messageFactory";
import {ClientErrorLogger} from "./utils/clientErrorLogger";
import {ChatMessageHandler} from "./chat/chatMessageHandler";
import {ChatEventHandler} from "./chat/chatEventHandler";
import {WidgetConfig} from "../types/config";
import ComponentConfig from "../config/componentConfig";
import {MessageHistory} from "./chat/messageHistory";
import {ChatBotSummary} from "./chat/chatBotSummary";
import {ResponseFactory} from "./factory/responseFactory";
import {ErrorTypes, MessageSendState} from "../types/enums";

// Typ für Kontext aus ClientErrorLogger
export type WebsocketErrorContext = {
    messageType?: string;
    reason?: string;
    websocket?: WebSocket;
};

export default abstract class AbstractWebsocketChat
{
    private messageHandler: ChatMessageHandler|null = null;
    private eventHandler: ChatEventHandler|null = null;
    protected chatConnector: AbstractChatConnector|null = null;

    protected constructor(
        protected config: WidgetConfig,
        protected componentConfig: ComponentConfig,
        protected alpineComponent: ComponentInterface,
    ) {
    }

    public abstract getChatConnector(): AbstractChatConnector;
    public abstract loadHistory():Promise<void>;
    protected abstract getWssUrl(): string;
    protected abstract getChatType(): string;
    protected abstract getSessionId(): null|string;
    protected abstract sendLeaveEvent(): Promise<void>;

    public async initConnection(userData: UserData|null = null, resetReconnectCounter:boolean = false):Promise<void> {
        if(userData instanceof UserData){
            this.getChatConnector().setUserData(userData);
        }
        await this.getChatConnector().initConnection(resetReconnectCounter);
    }

    public stopTimer(): void
    {
        // kann überschrieben werden
    }

    public async sendMessage(messageObject: BotMessageObject|AgentMessageObject, disableErrorHandling: boolean = false): Promise<void> {
        let webSocket = await this.getChatConnector().getWebSocket();
        if (webSocket === null) {
            return;
        }

        if (isBotMessageObject(messageObject)) {
            // UserData ist erst nach der websocket-initialisierung verfügbar
            let userData = this.getChatConnector().getUserData();
            if (userData === null) {
                (new ClientErrorLogger()).logInfo('userData not found for message object '+ messageObject.toString());
                return;
            }
            messageObject = MessageFactory.createBotSendMessage(messageObject, userData);
        }

        let errorHandler = (ws: WebSocket|undefined = undefined):void => { };
        if (!disableErrorHandling) {
            errorHandler = this.getMessageHandler().handleSendMessageError;
        }

        await trySendMessage(
            webSocket,
            messageObject,
            (ws: WebSocket|undefined = undefined) => errorHandler(ws),
            this.getMessageHandler(),
            this.getChatConnector()
        );

        await this.updateDisconnectTimer();
    }

    public async loadHistoryForCurrentChat():Promise<void> {
        let webSocket = await this.getChatConnector().getWebSocket();
        if (webSocket === null) {
            return;
        }

        waitForSocketConnection(
            webSocket,
            new ChatHistoryHandler().handleSendHistoryRequest,
            ():void => { },
            new HistoryMessage()
        );
    }

    public async closeConnection(): Promise<void> {
        this.stopTimer();
        this.sendLeaveEvent().then(():Promise<void> =>
            this.getChatConnector().closeConnection()
        );
    }

    public async closeWebsocketConnection(): Promise<void> {
        await this.getChatConnector().closeWebsocketConnection();
    }

    public closeWidget(): void {
        this.alpineComponent.closeWidget();
    }

    public async closeChatByAgent(): Promise<void> {
        this.alpineComponent.closeChatByAgent();
    }

    public async closeChat( isError: boolean|null = null): Promise<void> {
        this.alpineComponent.closeChat(isError);
    }

    public async minimize(): Promise<void> {
        this.getChatConnector().intentionalDisconnect = true;
        await this.closeWebsocketConnection();
        await this.getChatConnector().minimizeChat();
        this.alpineComponent.closeWidget();
        this.getChatConnector().intentionalDisconnect = false;
    }

    public handleWebsocketError(message: string = '', context?: WebsocketErrorContext): void
    {
        (new ClientErrorLogger()).logInfo('Websocket Error ' + message, 'error', context);
        this.setAnswerContent(ResponseFactory.create(
            'Die Funktion steht aktuell nicht zur Verfügung. Bitte versuchen Sie es später erneut. Wir bitten um Ihr Verständnis.',
            'error',
            '',
            '',
            '',
            true,
            false
        ));
    }

    public showBotSuggestions(): void
    {
        // nur im Bot-Chat relevant
    }

    public setAnswerContent(responseData: ResponseObject): void
    {
        this.alpineComponent.setAnswerContent(this.getChatType(), responseData);
    }

    public setHistoryContent(messageHistory:MessageHistory): void
    {
        this.alpineComponent.setHistoryContent(this.getChatType(), messageHistory);
    }

    public changeMessageStatus( id: string, status: MessageSendState ): void
    {
        this.alpineComponent.changeMessageStatus(id, status);
    }

    public handleInvalidChatSessionError(): void
    {
        this.alpineComponent.handleInvalidChatSessionError(ErrorTypes.INVALID_CHAT_SESSION);
    }

    public setBotSummary(summary: ChatBotSummary): void
    {

    }

    public isAgentChat(): boolean {
        return false;
    }

    public setAgentIsTyping(duration: number): void {
        // wird nur im agentChat gemacht
    }

    public async setAgentLeft(leftByBot: boolean = false): Promise<void> {
        // wird nur im agentChat gemacht
    }

    public hasSomeMessages(): boolean {
        return false;
    }

    public async updateDisconnectTimer(): Promise<void>
    {
        // wird nur im agentChat gemacht
    }

    public async waitForLoadedHistory(): Promise<void> {
        // wird nur im agentChat gemacht
    }

    public setHistoryLoaded(): void {
        // wird nur im agentChat gemacht
    }

    public async onBotHistoryLoaded(): Promise<void> {
        // wird nur im agentChat gemacht
    }

    protected getMessageHandler(): ChatMessageHandler
    {
        if (this.messageHandler === null) {
            this.messageHandler = new ChatMessageHandler(this);
        }

        return this.messageHandler;
    }

    protected getEventHandler(): ChatEventHandler
    {
        if (this.eventHandler === null) {
            this.eventHandler = new ChatEventHandler(this);
        }

        return this.eventHandler;
    }
}